import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const ACTION_LABELS = {
  "auth.login": { label: "Giriş yaptı", color: "text-slate-600" },
  "auth.password_change": { label: "Şifresini değiştirdi", color: "text-[#1B4776]" },
  "user.invite": { label: "Kullanıcı davet etti", color: "text-[#1B4776]" },
  "user.accept_invite": { label: "Daveti kabul etti", color: "text-emerald-700" },
  "user.invite_cancel": { label: "Daveti iptal etti", color: "text-slate-600" },
  "user.delete": { label: "Kullanıcı sildi", color: "text-red-700" },
  "supplier.create": { label: "Firma oluşturdu", color: "text-[#1B4776]" },
  "supplier.update": { label: "Firma güncelledi", color: "text-slate-700" },
  "supplier.delete": { label: "Firma sildi", color: "text-red-700" },
  "material.create": { label: "Malzeme kaydı ekledi", color: "text-[#1B4776]" },
  "material.delete": { label: "Malzeme kaydı sildi", color: "text-red-700" },
};

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

const detailText = (log) => {
  const d = log.detail || {};
  if (log.action === "material.create") {
    return `${d.material_name || ""} · ${d.quantity || ""} ${d.unit || ""} · ${d.supplier_name || ""}`;
  }
  if (log.action === "material.delete") {
    return `${d.material_name || ""} · ${d.quantity || ""}`;
  }
  if (log.action === "supplier.create" || log.action === "supplier.update" || log.action === "supplier.delete") {
    return d.name || "";
  }
  if (log.action === "user.invite") {
    return `${d.name || ""} (${d.role || ""})`;
  }
  if (log.action === "user.delete") {
    return d.email || "";
  }
  return "";
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/audit-logs`)
      .then(r => setLogs(r.data))
      .finally(() => setLoading(false));
  }, []);

  const { sort, toggleSort, sortedRows: sortedLogs } = useSortableRows(logs, {
    defaultKey: "timestamp", defaultDir: "desc",
    getters: {
      timestamp: (r) => r.timestamp || "",
      actor: (r) => r.actor_name || r.actor_email || "",
      action: (r) => (ACTION_LABELS[r.action]?.label || r.action || ""),
      detail: (r) => detailText(r),
    },
  });

  return (
    <Shell title="İşlem Geçmişi" subtitle="Denetim İzi">
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden" data-testid="audit-table">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <ClockCounterClockwise size={18} className="text-[#1B4776]" weight="duotone" />
          <div className="text-sm text-slate-600">
            <span className="font-bold text-slate-900">{logs.length}</span> işlem kaydı — son yapılanlar üstte
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-white">
                <SortableTh label="Zaman" k="timestamp" sort={sort} onSort={toggleSort} className="text-left px-6 py-3 w-40" />
                <SortableTh label="Kullanıcı" k="actor" sort={sort} onSort={toggleSort} className="text-left px-6 py-3 w-56" />
                <SortableTh label="İşlem" k="action" sort={sort} onSort={toggleSort} className="text-left px-6 py-3 w-56" />
                <SortableTh label="Detay" k="detail" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="py-12 text-center text-slate-500">Yükleniyor…</td></tr>
              )}
              {!loading && sortedLogs.length === 0 && (
                <tr><td colSpan={4} className="py-12 text-center text-slate-500">Henüz işlem kaydı yok.</td></tr>
              )}
              {sortedLogs.map((l, i) => {
                const meta = ACTION_LABELS[l.action] || { label: l.action, color: "text-slate-700" };
                return (
                  <tr key={l.id} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`audit-row-${i}`}>
                    <td className="px-6 py-3 font-mono text-xs text-slate-600">{fmt(l.timestamp)}</td>
                    <td className="px-6 py-3">
                      <div className="font-medium text-slate-900">{l.actor_name || "-"}</div>
                      <div className="text-xs text-slate-500 font-mono">{l.actor_email || ""}</div>
                    </td>
                    <td className={`px-6 py-3 font-medium ${meta.color}`}>{meta.label}</td>
                    <td className="px-6 py-3 text-slate-700 text-xs">{detailText(l)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}
